import React, { useState, useEffect } from 'react';
import { ApiService } from '../services/api';
import { QuickTip } from '../types';
import { Lightbulb } from 'lucide-react';

interface QuickTipsGalleryProps {
    onClose: () => void;
}

const QuickTipsGallery: React.FC<QuickTipsGalleryProps> = ({ onClose }) => {
    const [tips, setTips] = useState<QuickTip[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState<string>('Alle');
    const [currentTipIndex, setCurrentTipIndex] = useState(0);

    useEffect(() => {
        const loadTips = async () => {
            setLoading(true);
            try {
                const sysLists = await ApiService.getSystemLists('config');
                const tipsList = sysLists.find((l: any) => l.key === 'quicktips');
                if (tipsList && tipsList.value) {
                    setTips(tipsList.value);
                }
            } catch (e) {
                console.error("Error loading tips", e);
            } finally {
                setLoading(false);
            }
        };
        loadTips();
    }, []);

    // Reset index when category changes
    useEffect(() => {
        setCurrentTipIndex(0);
    }, [activeCategory]);

    const categories = ['Alle', ...Array.from(new Set(tips.map(t => t.category)))];
    const filteredTips = activeCategory === 'Alle'
        ? tips
        : tips.filter(t => t.category === activeCategory);

    // Sort by order
    filteredTips.sort((a, b) => a.order - b.order);

    const currentTip = filteredTips[currentTipIndex];

    const nextTip = () => {
        setCurrentTipIndex((prev) => (prev + 1) % filteredTips.length);
    };

    const prevTip = () => {
        setCurrentTipIndex((prev) => (prev - 1 + filteredTips.length) % filteredTips.length);
    };

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200" onClick={onClose}>
            <div className="bg-slate-900 border border-white/10 rounded-2xl sm:rounded-3xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] h-auto md:aspect-[16/9] flex flex-col shadow-2xl relative overflow-hidden" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="p-4 sm:p-6 border-b border-white/5 flex justify-between items-center bg-slate-900/50 backdrop-blur shrink-0 z-10">
                    <div>
                        <h2 className="text-lg sm:text-2xl font-black text-white uppercase tracking-tight flex items-center gap-2">
                            <span><Lightbulb className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1" /></span> Quicktips & Hilfe
                        </h2>
                        <p className="text-xs sm:text-sm text-slate-400 font-bold">Funktions-Beschreibungen</p>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-slate-800 hover:bg-slate-700 text-white flex items-center justify-center transition-colors">
                        ✕
                    </button>
                </div>

                {/* Categories */}
                <div className="px-3 sm:px-6 py-2.5 sm:py-4 flex gap-2 overflow-x-auto no-scrollbar border-b border-white/5 bg-slate-900/30 shrink-0 z-10">
                    {categories.map(cat => (
                        <button
                            key={cat}
                            onClick={() => setActiveCategory(cat)}
                            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold uppercase transition-all whitespace-nowrap shrink-0 ${activeCategory === cat
                                ? 'bg-sky-500 text-white shadow-lg shadow-sky-500/20'
                                : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
                                }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-hidden relative bg-slate-950/50 flex flex-col items-center justify-center p-2 sm:p-4 md:p-8">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-full text-slate-500 gap-4">
                            <div className="animate-spin text-4xl">⌛</div>
                            <p>Lade Tips...</p>
                        </div>
                    ) : filteredTips.length === 0 ? (
                        <div className="text-center py-20 text-slate-500">
                            Keine Tips in dieser Kategorie gefunden.
                        </div>
                    ) : (
                        <div className="w-full h-full flex flex-col relative max-w-4xl mx-auto">

                            {/* Navigation Buttons (Desktop) */}
                            <button
                                onClick={prevTip}
                                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-20 w-12 h-12 bg-slate-800/80 hover:bg-sky-500 text-white rounded-full flex items-center justify-center transition-all backdrop-blur shadow-xl border border-white/10 hidden md:flex"
                            >
                                ◀
                            </button>
                            <button
                                onClick={nextTip}
                                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 z-20 w-12 h-12 bg-slate-800/80 hover:bg-sky-500 text-white rounded-full flex items-center justify-center transition-all backdrop-blur shadow-xl border border-white/10 hidden md:flex"
                            >
                                ▶
                            </button>

                            {/* Card */}
                            <div className="flex-1 bg-slate-900 border border-white/10 rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row animate-in fade-in zoom-in-95 duration-300 transform-gpu relative">

                                {/* Image Section */}
                                <div className="md:w-1/2 h-44 sm:h-64 md:h-auto relative bg-slate-950 border-b md:border-b-0 md:border-r border-white/5 group shrink-0">
                                    {currentTip.image_url ? (
                                        <img
                                            src={currentTip.image_url}
                                            alt={currentTip.title}
                                            className="w-full h-full object-cover md:absolute inset-0 transition-transform duration-700 group-hover:scale-105"
                                            // eslint-disable-next-line react/forbid-component-props
                                            style={{ objectPosition: `${currentTip.image_position?.x ?? 50}% ${currentTip.image_position?.y ?? 50}%` }}
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-6xl opacity-20">
                                            <Lightbulb className="w-4 h-4 inline mr-1" />
                                        </div>
                                    )}
                                    {/* Category Badge overlay on image */}
                                    <div className="absolute top-3 left-3 sm:top-4 sm:left-4">
                                        <span className="text-[10px] sm:text-xs font-black uppercase tracking-wider text-white bg-black/60 backdrop-blur px-2.5 py-1 rounded-full border border-white/10">
                                            {currentTip.category}
                                        </span>
                                    </div>
                                </div>

                                {/* Content Section */}
                                <div className="md:w-1/2 p-4 sm:p-6 md:p-8 flex flex-col overflow-y-auto custom-scrollbar">
                                    <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-white mb-2 sm:mb-4 leading-tight">
                                        {currentTip.title}
                                    </h3>
                                    <div className="prose prose-invert prose-sm text-slate-300 leading-relaxed flex-1">
                                        <p className="whitespace-pre-wrap">{currentTip.description}</p>
                                    </div>

                                    {/* Video Link */}
                                    {currentTip.video_url && (
                                        <div className="mt-6 pt-6 border-t border-white/5">
                                            <a
                                                href={currentTip.video_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-2 text-sky-400 hover:text-white transition-colors font-bold uppercase text-xs tracking-wider"
                                            >
                                                <span>▶</span> Video-Anleitung ansehen
                                            </a>
                                        </div>
                                    )}

                                    {/* Mobile Navigation Controls */}
                                    <div className="mt-6 flex justify-between items-center md:hidden pt-4 border-t border-white/5">
                                        <button onClick={prevTip} className="px-4 py-2 bg-slate-800 rounded-lg text-sm text-white">Zurück</button>
                                        <span className="text-xs text-slate-500 font-mono">{currentTipIndex + 1} / {filteredTips.length}</span>
                                        <button onClick={nextTip} className="px-4 py-2 bg-sky-600 rounded-lg text-sm text-white">Weiter</button>
                                    </div>
                                </div>
                            </div>

                            {/* Dots Indicator */}
                            <div className="h-12 flex items-center justify-center gap-2 shrink-0">
                                {filteredTips.map((_, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setCurrentTipIndex(idx)}
                                        aria-label={`Gehe zu Tipp ${idx + 1}`}
                                        className={`w-2 h-2 rounded-full transition-all ${idx === currentTipIndex
                                            ? 'bg-sky-500 w-6'
                                            : 'bg-slate-700 hover:bg-slate-500'
                                            }`}
                                    />
                                ))}
                            </div>

                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default QuickTipsGallery;
